import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  Chip,
  alpha,
  Divider
} from '@mui/material';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import SecurityIcon from '@mui/icons-material/Security';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LoginIcon from '@mui/icons-material/Login';
import DashboardIcon from '@mui/icons-material/Dashboard';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { getPublicApiBase } from '../../utils/publicApi';
import { LandingPageAppearanceProvider, useLandingPageAppearance } from '../../context/LandingPageAppearanceContext';
import { getPageHeroTextColors, panelOutlinedButtonSx } from '../../utils/landingThemeHelpers';
import PublicSiteNav from './PublicSiteNav';
import CareerJourneySection from './CareerJourneySection';
import LandingTemplateSelector from './LandingTemplateSelector';
import FlipFeatureCard from './FlipFeatureCard';
import { PLATFORM_FEATURES } from './platformFeaturesData';

const HIGHLIGHTS = [
  { label: 'Multi-tenant ready', icon: <CloudQueueIcon fontSize="small" /> },
  { label: 'RBAC & audit trail', icon: <SecurityIcon fontSize="small" /> },
  { label: 'Reports & analytics', icon: <AnalyticsIcon fontSize="small" /> }
];

function ProductLandingPageContent() {
  const navigate = useNavigate();
  const { pageTemplateId, pageTheme, panelsTheme, featuresTheme, navBarVariant } = useLandingPageAppearance();
  const [tagline, setTagline] = useState('Enterprise resource planning for consulting and staffing teams.');

  const heroText = getPageHeroTextColors(pageTemplateId, pageTheme);
  const panelTextPrimary = panelsTheme.textPrimary;
  const panelTextSecondary = panelsTheme.textSecondary;
  const accent = panelsTheme.decorColor1;
  const ctaBg = '#6366f1';
  const ctaHover = '#4f46e5';
  const panelOutlineSx = panelOutlinedButtonSx(panelsTheme);

  useEffect(() => {
    let cancelled = false;
    const base = getPublicApiBase().replace(/\/+$/, '');
    axios
      .get(`${base}/branding`)
      .then((r) => {
        const t = r.data?.data?.companyTagline;
        if (!cancelled && t && String(t).trim()) setTagline(String(t).trim());
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const goLogin = (entryLabel, redirectPath) => {
    navigate('/login', { state: { from: { pathname: redirectPath }, entryLabel } });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: pageTheme.background,
        color: heroText.primary,
        transition: 'background 0.45s ease, color 0.35s ease'
      }}
    >
      <LandingTemplateSelector />

      <PublicSiteNav barVariant={navBarVariant} active="home" />

      {/* Hero */}
      <Container maxWidth="lg" sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 4, md: 6 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={7}>
            <Chip
              label="ERP4C · Base tenant platform"
              size="small"
              sx={{
                mb: 2,
                bgcolor: alpha(accent, 0.35),
                color: heroText.primary,
                fontWeight: 600,
                border: '1px solid',
                borderColor: panelsTheme.panelBorder
              }}
            />
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
                mb: 2,
                color: heroText.primary
              }}
            >
              Run your consulting business on one connected platform
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: heroText.secondary, fontWeight: 400, lineHeight: 1.6, mb: 3, maxWidth: 560 }}
            >
              {tagline}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
              {HIGHLIGHTS.map((h) => (
                <Chip
                  key={h.label}
                  icon={h.icon}
                  label={h.label}
                  variant="outlined"
                  sx={{
                    color: heroText.primary,
                    borderColor: panelsTheme.panelBorder,
                    '& .MuiChip-icon': { color: heroText.secondary }
                  }}
                />
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                size="large"
                startIcon={<DashboardIcon />}
                onClick={() => goLogin('Dashboard', '/dashboard')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  py: 1.25,
                  bgcolor: ctaBg,
                  boxShadow: `0 8px 24px ${alpha(ctaBg, 0.45)}`,
                  '&:hover': { bgcolor: ctaHover }
                }}
              >
                Sign in to dashboard
              </Button>
              <Button
                variant="outlined"
                size="large"
                startIcon={<SearchIcon />}
                onClick={() => navigate('/query')}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  color: heroText.primary,
                  borderColor: alpha(heroText.primary, 0.45),
                  '&:hover': { borderColor: heroText.primary, bgcolor: alpha(heroText.primary, 0.08) }
                }}
              >
                Open query page
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: panelsTheme.cardBg,
                border: '1px solid',
                borderColor: panelsTheme.cardBorder,
                boxShadow: panelsTheme.panelShadow,
                transition: 'background-color 0.35s ease, border-color 0.35s ease'
              }}
            >
              <Typography variant="overline" sx={{ color: panelTextSecondary, fontWeight: 700, letterSpacing: 0.12 }}>
                Get started
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: 0.5, color: panelTextPrimary }}>
                Choose how you want to enter
              </Typography>
              <Stack spacing={1.5}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DashboardIcon />}
                  onClick={() => goLogin('Dashboard', '/dashboard')}
                  sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.25, bgcolor: ctaBg, color: '#fff' }}
                >
                  Dashboard — employees & managers
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<LoginIcon />}
                  onClick={() => goLogin('Sign in', '/dashboard')}
                  sx={{ justifyContent: 'flex-start', py: 1.25, ...panelOutlineSx }}
                >
                  Sign in / Sign up — existing accounts
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={() => navigate('/signup')}
                  sx={{ justifyContent: 'flex-start', py: 1.25, ...panelOutlineSx }}
                >
                  Register / Signup — create a new account
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={() => navigate('/query')}
                  sx={{ justifyContent: 'flex-start', py: 1.25, ...panelOutlineSx }}
                >
                  Query page — browse jobs & training (no login)
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <CareerJourneySection />

      {/* Features */}
      <Box
        sx={{
          bgcolor: alpha(featuresTheme.panelBg, 0.92),
          py: { xs: 6, md: 8 },
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: featuresTheme.panelBorder,
          transition: 'background-color 0.35s ease'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h4" sx={{ fontWeight: 800, textAlign: 'center', mb: 1, color: featuresTheme.textPrimary }}>
            Everything your team needs
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: featuresTheme.textSecondary,
              mb: 1,
              maxWidth: 720,
              mx: 'auto'
            }}
          >
            ERP4C connects sales, delivery, HR, and compliance workflows so data flows once and stays auditable.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: alpha(featuresTheme.textSecondary, 0.9),
              mb: 4,
              fontWeight: 600
            }}
          >
            {PLATFORM_FEATURES.length} capability areas — click any card for a live demo preview
          </Typography>
          <Grid container spacing={2}>
            {PLATFORM_FEATURES.map((f, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={f.id}>
                <FlipFeatureCard feature={f} colorIndex={index} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: heroText.primary }}>
          Ready to work inside the platform?
        </Typography>
        <Typography variant="body1" sx={{ color: heroText.secondary, mb: 3 }}>
          Administrators configure the tenant; your team uses the dashboard day to day. Visitors can explore open jobs
          and training on the query page.
        </Typography>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
          onClick={() => goLogin('Dashboard', '/dashboard')}
          sx={{ textTransform: 'none', fontWeight: 700, px: 4, bgcolor: ctaBg, '&:hover': { bgcolor: ctaHover } }}
        >
          Go to login
        </Button>
      </Container>

      <Divider sx={{ borderColor: alpha(heroText.primary, 0.12) }} />
      <Box component="footer" sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: alpha(heroText.secondary, 0.85) }}>
          ERP4C · Enterprise consulting platform · Secure sign-in required for internal data
        </Typography>
      </Box>
    </Box>
  );
}

export default function ProductLandingPage() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <LandingPageAppearanceProvider>
      <ProductLandingPageContent />
    </LandingPageAppearanceProvider>
  );
}
